/**
 * Base Action Modal Component
 * 
 * Abstract base for all arbitration action modals
 * Follows Open/Closed Principle - extend without modifying
 */

import React from 'react';
import { Button, Typography, CircularProgress } from '@mui/material';
import ResponsiveModal from '../../common/ResponsiveModal';
import { ArbitrationAction, ActionLabels } from '../../../services/arbitrationService';

// Interface Segregation - small focused interfaces
export interface IActionModalProps {
  open: boolean;
  onClose: () => void;
  onConfirm: (payload: any) => Promise<void>;
  action: ArbitrationAction;
  disputeTitle: string;
  loading?: boolean;
  error?: string | null;
}

export interface IModalChildProps {
  payload: any;
  setPayload: React.Dispatch<React.SetStateAction<any>>;
  isValid: boolean;
  setIsValid: React.Dispatch<React.SetStateAction<boolean>>;
}

// Action colors for visual feedback
const ActionColors: Record<ArbitrationAction, 'primary' | 'success' | 'error' | 'warning' | 'info'> = {
  [ArbitrationAction.APPROVE]: 'success',
  [ArbitrationAction.REJECT]: 'error',
  [ArbitrationAction.SEND_MESSAGE]: 'primary',
  [ArbitrationAction.CLOSE_CHAT]: 'warning',
  [ArbitrationAction.REFUND_ORDER]: 'info',
  [ArbitrationAction.RESOLVE]: 'success',
  [ArbitrationAction.REFUND_ITEM]: 'info',
  [ArbitrationAction.CHANGE_ITEM_STATUS]: 'primary',
  [ArbitrationAction.FORCE_DELIVER]: 'success',
  [ArbitrationAction.REQUEST_REDELIVERY]: 'warning',
  [ArbitrationAction.REFUND_INSPECTION_FEE]: 'info',
  [ArbitrationAction.OVERRIDE_INSPECTION]: 'warning',
  [ArbitrationAction.PENALIZE_INSPECTOR]: 'error'
};

// Action icons
const ActionIcons: Record<ArbitrationAction, string> = {
  [ArbitrationAction.APPROVE]: '✅',
  [ArbitrationAction.REJECT]: '❌',
  [ArbitrationAction.SEND_MESSAGE]: '💬',
  [ArbitrationAction.CLOSE_CHAT]: '🔒',
  [ArbitrationAction.REFUND_ORDER]: '💰',
  [ArbitrationAction.RESOLVE]: '🤝',
  [ArbitrationAction.REFUND_ITEM]: '💳',
  [ArbitrationAction.CHANGE_ITEM_STATUS]: '🔄',
  [ArbitrationAction.FORCE_DELIVER]: '📦',
  [ArbitrationAction.REQUEST_REDELIVERY]: '🔁',
  [ArbitrationAction.REFUND_INSPECTION_FEE]: '🧾',
  [ArbitrationAction.OVERRIDE_INSPECTION]: '🔍',
  [ArbitrationAction.PENALIZE_INSPECTOR]: '⚠️'
};

/**
 * BaseActionModal - Template Method Pattern
 * Provides common structure, children provide specific content
 */
const BaseActionModal: React.FC<IActionModalProps & { children: React.ReactNode }> = ({
  open,
  onClose,
  onConfirm,
  action,
  disputeTitle,
  loading = false,
  error = null,
  children
}) => {
  const [payload, setPayload] = React.useState<any>({});
  const [isValid, setIsValid] = React.useState(false);

  const handleConfirm = async () => {
    await onConfirm(payload);
  };

  const handleClose = () => {
    if (!loading) {
      setPayload({});
      setIsValid(false);
      onClose();
    }
  };

  const color = ActionColors[action] || 'primary';
  const icon = ActionIcons[action] || '📋';
  const label = ActionLabels[action] || action;

  return (
    <ResponsiveModal
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      loading={loading}
      error={error}
      disableClose={loading}
      icon={
        <Typography variant="h5" component="span" sx={{ lineHeight: 1 }}>
          {icon}
        </Typography>
      }
      title={label}
      subtitle={disputeTitle}
      actions={
        <>
          <Button onClick={handleClose} disabled={loading} variant="outlined" color="inherit">
            انصراف
          </Button>
          <Button
            onClick={handleConfirm}
            disabled={loading || !isValid}
            variant="contained"
            color={color}
            startIcon={loading && <CircularProgress size={20} color="inherit" />}
          >
            {loading ? 'در حال انجام...' : 'تأیید'}
          </Button>
        </>
      }
    >
      {/* Render children with context */}
      {React.Children.map(children, (child) => {
        if (React.isValidElement(child)) {
          return React.cloneElement(child as React.ReactElement<IModalChildProps>, {
            payload,
            setPayload,
            isValid,
            setIsValid,
          });
        }
        return child;
      })}
    </ResponsiveModal>
  );
};

export default BaseActionModal;

